import { FC, ReactNode } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Button, InlineNotification } from '@frontend/design-system';

type Props = {
  isDirty: boolean;
  hasErrors?: boolean;
  dirtyBadge?: ReactNode;
  errorBadge?: ReactNode;
  extraContent?: ReactNode;
  primaryAction: {
    label: string;
    hoverLabel?: string;
    onClick: () => void;
    trackingId?: string;
    disabled?: boolean;
    isLoading?: boolean;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
    trackingId?: string;
    disabled?: boolean;
    isLoading?: boolean;
  };
  styleOverrides?: Interpolation<Theme>;
};

export const FloatingSaveBar: FC<Props> = ({
  isDirty,
  dirtyBadge,
  errorBadge,
  hasErrors,
  extraContent,
  primaryAction,
  secondaryAction,
  styleOverrides,
}) => {
  const { t } = useTranslation('settings');

  return (
    <div css={[styles.floatingCard, styleOverrides]}>
      <div>
        {hasErrors
          ? errorBadge ?? (
              <InlineNotification
                status='error'
                message={t('You must address the error(s) above before saving.')}
                size='medium'
              />
            )
          : isDirty
          ? dirtyBadge ?? <InlineNotification status='warn' message={t('Unsaved changes')} size='medium' />
          : null}
      </div>

      <div css={styles.actionButtons}>
        {extraContent}
        {secondaryAction && (
          <Button
            variant='secondary'
            onClick={secondaryAction.onClick}
            disabled={secondaryAction.disabled}
            loading={secondaryAction.isLoading}
            trackingId={secondaryAction.trackingId}
          >
            {secondaryAction.label || t('Cancel')}
          </Button>
        )}
        <Button
          variant='primary'
          onClick={primaryAction.onClick}
          hoverLabel={primaryAction.hoverLabel}
          disabled={!isDirty || primaryAction.disabled}
          loading={primaryAction.isLoading}
          trackingId={primaryAction.trackingId}
        >
          {primaryAction.label || t('Save')}
        </Button>
      </div>
    </div>
  );
};

const styles = {
  floatingCard: css`
    position: sticky;
    bottom: var(--fabric-number-n-4-px);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--fabric-space-padding-xs);
    background-color: var(--fabric-card-background);
    box-shadow: var(--fabric-modal-shadow);
    z-index: 1;
    border-radius: var(--fabric-radius-md);
    max-width: 700px;
  `,
  actionButtons: css`
    display: flex;
    gap: var(--fabric-space-gap-xs);
    align-items: center;
  `,
};
